import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, Clock, CheckCircle, AlertTriangle, CreditCard, FileText, Truck, RotateCcw, Shield, Phone, Mail } from 'lucide-react';

const CancellationRefundPolicyPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Cancellation & Refund Policy | Strum Music";
  }, []);

  const sideNavItems = [
    {
      title: 'Privacy Policy',
      href: '/privacy-policy',
      icon: <Shield className="h-4 w-4" />
    },
    {
      title: 'Terms & Conditions',
      href: '/terms-conditions',
      icon: <FileText className="h-4 w-4" />
    },
    {
      title: 'Shipping & Delivery Policy',
      href: '/shipping-delivery-policy',
      icon: <Truck className="h-4 w-4" />
    },
    {
      title: 'Cancellation & Refund Policy',
      href: '/cancellation-refund-policy',
      icon: <XCircle className="h-4 w-4" />
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-red-700 py-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-200" />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Cancellation & Refund Policy</h1>
            <p className="text-xl text-red-100 max-w-2xl mx-auto">
              Understand our order cancellation, return, and refund terms
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-12">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Policies</h2>
                <nav className="space-y-2">
                  {sideNavItems.map((item, index) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={index}
                        to={item.href}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-red-100 text-red-700 border-l-4 border-red-700'
                            : 'text-gray-600 hover:text-red-700 hover:bg-red-50'
                        }`}
                      >
                        {item.icon}
                        <span>{item.title}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-8"
              >
                {/* Overview */}
                <div>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    We understand that sometimes you may need to cancel your order or return a product. This policy outlines 
                    the terms and conditions for order cancellations, returns, exchanges, and the refund process.
                  </p>
                  <p className="text-gray-600 text-sm">
                    <strong>Last updated:</strong> January 2025
                  </p>
                </div>

                {/* Order Cancellation */}
                <div>
                  <div className="flex items-center mb-4">
                    <XCircle className="h-6 w-6 text-red-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Order Cancellation</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-3">When You Can Cancel</h3>
                      <p className="text-green-700 mb-4">
                        You can cancel your order free of charge in the following situations:
                      </p>
                      <ul className="space-y-2 text-green-700">
                        <li>• <strong>Before order confirmation:</strong> Immediately after placing the order</li>
                        <li>• <strong>Within 2 hours:</strong> Of order confirmation (if not yet processed)</li>
                        <li>• <strong>Before dispatch:</strong> If the order hasn't been shipped yet</li>
                        <li>• <strong>Out of stock:</strong> If the item becomes unavailable</li>
                      </ul>
                    </div>

                    <div className="bg-red-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-red-800 mb-3">When Cancellation is Not Allowed</h3>
                      <p className="text-red-700 mb-4">
                        Cancellation may not be possible in the following situations:
                      </p>
                      <ul className="space-y-2 text-red-700">
                        <li>• <strong>After dispatch:</strong> Once the order has been shipped</li>
                        <li>• <strong>Custom orders:</strong> Specially customized or personalized items</li>
                        <li>• <strong>Digital products:</strong> Software or digital downloads already delivered</li>
                        <li>• <strong>Rental items:</strong> Once rental period has started</li>
                        <li>• <strong>Sale items:</strong> Final sale or clearance products (marked as non-returnable)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Returns & Exchanges */}
                <div>
                  <div className="flex items-center mb-4">
                    <RotateCcw className="h-6 w-6 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Returns & Exchanges</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-3">Return Eligibility</h3>
                      <p className="text-gray-700 mb-4">
                        Returns or exchanges accepted within <strong>7 days</strong> of delivery if:
                      </p>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                          <span>Item is damaged or defective</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                          <span>Incorrect item delivered</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                          <span>Product has manufacturing fault</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-red-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-red-800 mb-3">Non-returnable Items</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                          <span>Accessories like strings, picks, reeds, etc.</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                          <span>Downloadable software and digital products</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                          <span>Factory returned or rental products unless faulty</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* How to Cancel/Return */}
                <div>
                  <div className="flex items-center mb-4">
                    <Clock className="h-6 w-6 text-blue-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">How to Cancel or Return</h2>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    {[
                      {
                        step: "1",
                        title: "Contact Us Immediately",
                        description: "Call or email us with your order number as soon as possible.",
                        icon: "📞"
                      },
                      {
                        step: "2", 
                        title: "Provide Order Details",
                        description: "Share your order number, registered email, and reason for cancellation/return.",
                        icon: "📋"
                      },
                      {
                        step: "3",
                        title: "Get Confirmation",
                        description: "We'll confirm the cancellation/return and provide further instructions.",
                        icon: "✅"
                      }
                    ].map((step, index) => (
                      <div key={index} className="text-center p-6 border border-gray-200 rounded-lg">
                        <div className="text-4xl mb-4">{step.icon}</div>
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                          {step.step}
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-2">{step.title}</h4>
                        <p className="text-gray-600 text-sm">{step.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2 text-blue-700">
                        <Phone className="h-4 w-4" />
                        <span><strong>Phone:</strong> +91-8076883098</span>
                      </div>
                      <div className="flex items-center space-x-2 text-blue-700">
                        <Mail className="h-4 w-4" />
                        <span><strong>Email:</strong> strum.music.co@gmail.com</span>
                      </div>
                    </div>
                    <p className="text-blue-700 text-sm mt-3">
                      <strong>Business Hours:</strong> Tuesday - Sunday: 11:30am - 8:30pm (Closed Mondays)
                    </p>
                  </div>
                </div>

                {/* Refund Process */}
                <div>
                  <div className="flex items-center mb-4">
                    <CreditCard className="h-6 w-6 text-purple-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">Refund Process</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-800 mb-3">Refund Timeline</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-purple-700 mb-2">Online Payments</h4>
                          <ul className="space-y-1 text-purple-700 text-sm">
                            <li>• Credit/Debit Cards: 5-7 business days</li>
                            <li>• UPI/Net Banking: 3-5 business days</li>
                            <li>• Digital Wallets: 2-4 business days</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-purple-700 mb-2">Processing Time</h4>
                          <ul className="space-y-1 text-purple-700 text-sm">
                            <li>• Cancellation confirmation: Within 24 hours</li>
                            <li>• Refund initiation: 1-2 business days</li>
                            <li>• Bank processing: As per payment method</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-3">Refund Amount</h3>
                      <ul className="space-y-2 text-yellow-700">
                        <li>• <strong>Full refund:</strong> If cancelled before dispatch or item is defective</li>
                        <li>• <strong>Shipping charges:</strong> Non-refundable unless order was cancelled by us</li>
                        <li>• <strong>Payment gateway charges:</strong> May be deducted as per bank policies</li>
                        <li>• <strong>Promotional discounts:</strong> Will be adjusted in refund amount</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Special Cases */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Special Cases</h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <RotateCcw className="h-5 w-5 mr-2 text-blue-600" />
                        Rental Orders
                      </h3>
                      <p className="text-gray-700 text-sm">
                        Rental orders can be cancelled up to 24 hours before the scheduled pickup/delivery time. 
                        Cancellations after this period may incur charges.
                      </p>
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                        Bulk Orders
                      </h3>
                      <p className="text-gray-700 text-sm">
                        Orders with multiple items may be partially cancelled if some items are already processed. 
                        Refund will be calculated accordingly.
                      </p>
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                        EMI Orders
                      </h3>
                      <p className="text-gray-700 text-sm">
                        For EMI purchases, cancellation will stop future EMI deductions. Any processing fees 
                        charged by the bank are non-refundable.
                      </p>
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <XCircle className="h-5 w-5 mr-2 text-red-600" />
                        Force Majeure
                      </h3>
                      <p className="text-gray-700 text-sm">
                        Orders may be cancelled by us due to unforeseen circumstances like natural disasters, 
                        strikes, or supply chain disruptions. Full refund will be provided.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-600">
                  <div className="flex items-center mb-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mr-2" />
                    <h3 className="text-lg font-semibold text-orange-800">Important Notes</h3>
                  </div>
                  <ul className="space-y-2 text-orange-700">
                    <li>• Cancellation/return requests must be made during business hours for immediate processing</li>
                    <li>• Refunds will be processed to the original payment method only</li>
                    <li>• Bank charges for international transactions are non-refundable</li>
                    <li>• Confirmation will be sent via email and SMS</li>
                    <li>• For urgent requests, call us directly for faster processing</li>
                    <li>• Items must be in original condition with all accessories and documentation</li>
                  </ul>
                </div>

                {/* Contact for Support */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>
                  <p className="text-gray-700 mb-4">
                    Contact us immediately for the fastest processing. Our team is ready to assist you.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a 
                      href="tel:+918076883098" 
                      className="btn btn-primary py-2 px-4 text-center flex items-center justify-center space-x-2"
                    >
                      <Phone className="h-4 w-4" />
                      <span>Call: +91-8076883098</span>
                    </a>
                    <a 
                      href="mailto:strum.music.co@gmail.com" 
                      className="btn btn-outline py-2 px-4 text-center flex items-center justify-center space-x-2"
                    >
                      <Mail className="h-4 w-4" />
                      <span>Email: strum.music.co@gmail.com</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CancellationRefundPolicyPage;